// Pošta: účty a složky vlevo (+ „Vše – doručená“), seznam zpráv s hledáním a filtry, náhled vpravo/dole/vypnutý,
// čtení HTML v sandboxovaném iframe, příznaky, přesuny, mazání. Data: akce okna `posta` (list, message, flags, move, delete, sync).
import { registerComponent } from '@core/views/factory';
import './stats';
import { action, core, upload, ApiError } from '@core/api';
import { h, clear, debounce } from '@core/dom';
import { t, tk, fmtDate, fmtTime, fmtSize } from '@core/i18n';
import { icon } from '@core/icons';
import { IwRibbon, type RibbonButton } from '@core/components/ribbon';
import { IwCommandBar, type BarItem } from '@core/components/commandbar';
import { useBar, renderBarHeader, fabButton, renderSelBar, filterChip, clearFiltersChip, HeadSearch } from '@core/components/chrome';
import { IwSplit, emptyState, statusBar } from '@core/components/misc';
import { contextMenu, IwMenu, type MenuItem } from '@core/components/menu';
import { confirm, prompt, dialog, confirmDelete } from '@core/components/dialog';
import { toast } from '@core/components/toast';
import { events } from '@core/events';
import { PagedRows } from '@core/components/paged';
import { isPh, headWindow, headOffset, rangeIds } from '@core/components/virtual';
import type { Row as CoreRow } from '@core/components/table';
import './compose';
import './zprava';
import { senderListable, senderMenu } from './listina';

import type { Folder, Account, Row, Message, Label } from './types';
import { ROLE_ICON } from './types';
import { renderMessage, folderName, initials, hue, openSource, openHeaders, openAttachment, labelChips, printMessage, userLabels, sendIcon } from './message';

interface Payload { accounts: Account[]; outbox?: number; labels?: Label[]; canManage: boolean; held?: number | null; prefs: Record<string, unknown>; folder: string | null; message: string | null }
type PreviewPos = 'right' | 'bottom' | 'off';

registerComponent('posta-view', ({ desc, win, wm }) => {
  const p = desc.payload as unknown as Payload;
  let accounts = p.accounts;
  let outboxCount = p.outbox ?? 0; // K odeslání: zprávy ve frontě, zadržené a neodeslané
  const prefs = p.prefs;
  let previewPos: PreviewPos = (prefs['preview'] as PreviewPos) ?? 'right';
  const collapsed = new Set<string>(Array.isArray(prefs['collapsed']) ? (prefs['collapsed'] as string[]) : []);
  const collapsedFolders = new Set<string>(Array.isArray(prefs['collapsedFolders']) ? (prefs['collapsedFolders'] as string[]) : []);
  const prefix = (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';
  let folderKey = p.folder ?? (typeof prefs['lastFolder'] === 'string' ? (prefs['lastFolder'] as string) : 'all');
  let filter = '';
  let q = '';
  // řazení (uživatelská volba společná pro všechny složky): sloupec, směr, „nejdřív“
  const sortPref = (prefs['sort'] && typeof prefs['sort'] === 'object' ? prefs['sort'] : {}) as { col?: string; dir?: string; first?: string };
  let sortCol: 'date' | 'from' | 'subject' | 'size' = (['date', 'from', 'subject', 'size'] as const).find((x) => x === sortPref.col) ?? 'date';
  let sortDir: 'asc' | 'desc' = sortPref.dir === 'asc' ? 'asc' : 'desc';
  let groupByDate = prefs['groupByDate'] === true;
  let threads = prefs['threads'] === true;
  // štítky = klíčová slova IMAP; výchozích pět jako Thunderbird ($label1–5), uživatel si je přejmenuje/přibarví/přidá (pref `labels`)
  let labels: Label[] = p.labels ?? userLabels(prefs); // seznam štítků jádra (Nastavení → Štítky), společný s Kontakty // konverzace: řádek = vlákno (nejnovější zpráva + počet), náhled = celé vlákno // nadpisy Dnes / Včera / Tento týden… v seznamu řazeném podle data
  let sortFirst: '' | 'unread' | 'flagged' | 'attachments' = (['unread', 'flagged', 'attachments'] as const).find((x) => x === sortPref.first) ?? '';
  let page = 1;
  let total = 0;
  let rows: Row[] = [];
  // plynulý seznam (nové ovládání, vzor Kontakty – bod C1): celá složka jako pole, nenačtené pozice jsou zástupci, vykreslí
  // se jen okno kolem posunu a dávky se dočtou podle toho, co je vidět. Nadpisy skupin podle data stojí na pozicích
  // spočtených serverem (marks), takže se seznam při dočítání neposouvá.
  const ph = (r: Row | undefined): boolean => isPh(r as unknown as CoreRow);
  let heads: { pos: number; key: string }[] = [];
  let loadSeq = 0;
  let rowH = 0;
  let headH = 0;
  let measuring = false;
  let vwin = { start: -1, end: -1 };
  let vraf = 0;
  const paged = new PagedRows<Row>({
    pageSize: 50,
    rowId: 'id',
    fetch: (pg) => fetchPage(pg),
    onUpdate: () => { rows = paged.rows; renderWindow(true); },
    onTotalChanged: () => reloadSoon(), // přibylo / ubylo – načíst znovu se zachováním místa
    onError: (e) => toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'),
    onBusy: () => renderStatus(),
  });
  const selected = new Set<string>();
  let cursor: string | null = null;
  let current: Message | null = null;
  let narrow = false;
  let sideHidden = false;
  let showingPreview = false; // úzké okno: seznam, nebo náhled
  // nové ovládání (vzor Kontakty, podklady/vzor-kontakty-kontrolni-seznam.md): náhled jen v dost širokém okně na PC,
  // telefon otevírá zprávu jako samostatnou stránku (zpět ji zavře), štítek je filtr nad seznamem, dlouhý stisk = režim výběru
  let detailOn = false;
  let labelKw = '';
  let selectMode = false;
  let backSel = 0;
  let suppressTap = 0;
  const isMobile = (): boolean => wm.currentMode() === 'mobile';
  const wantDetail = (): boolean => !isMobile() && previewPos !== 'off' && el.clientWidth >= 860;

  // ---- kostra
  const ribbon = new IwRibbon();
  ribbon.setMode(wm.currentMode());
  const split = new IwSplit();
  const side = h('div', { class: 'mail-side' });
  const banners = h('div', { class: 'mail-banners' });
  const listWrap = h('div', { class: 'mail-list-wrap' });
  const preview = h('div', { class: 'mail-preview' });
  const inner = new IwSplit();
  inner.onResize = (px) => savePref('listWidth', px);
  const innerV = new IwSplit(); // náhled dole: seznam nahoře, náhled pod ním, táhlo mezi nimi
  innerV.setVertical(true);
  innerV.onResize = (px) => savePref('listHeight', px);
  const content = h('div', { class: 'mail-content' });
  split.first.appendChild(side);
  split.second.appendChild(content);
  split.setWidth(240);
  // koncepce A (postup 69): záhlaví ☰ (složky) · Pošta · Nová zpráva · akce nad zprávou · ⋯ · ⚙; telefon ⋮ + plovoucí Nová zpráva
  const bar = useBar() ? new IwCommandBar() : null;
  const fab = fabButton();
  const selBarEl = h('div', { class: 'sel-bar', hidden: true });
  const el = bar ? h('div', { class: 'view mail-view chrome-bar' }, split, selBarEl, fab) : h('div', { class: 'view mail-view' }, ribbon, split);
  if (bar) { // vysunutý panel složek (úzké okno): klepnutí vedle ho zavře
    const backdrop = h('div', { class: 'side-backdrop' });
    backdrop.addEventListener('click', () => setSide(true));
    split.second.appendChild(backdrop);
  }
  if (bar) bar.globalLabel = t('posta.name');
  let backSide = 0;
  /** Panel složek: na širokém okně se schová / ukáže, na úzkém se vysune přes seznam (zpět ho zavře). */
  function setSide(hide: boolean): void {
    sideHidden = hide;
    split.setFirstHidden(hide);
    el.classList.toggle('side-open', !hide && narrow);
    if (!hide && narrow && !wm.hasBack(backSide)) backSide = wm.pushBack(() => { backSide = 0; setSide(true); return true; }, win);
    if (hide && backSide) {
      const id = backSide;
      backSide = 0;
      wm.dropBack(id);
    }
    if (bar) refreshRibbon();
  }
  const ro = new ResizeObserver(() => {
    const n = el.clientWidth > 0 && el.clientWidth < 760;
    if (!el.clientWidth) return;
    if (n === narrow) {
      if (bar && wantDetail() !== detailOn) { layout(); refreshRibbon(); } // náhled se zapne / vypne podle šířky okna
      return;
    }
    narrow = n;
    split.setOverlay(n);
    if (bar) setSide(n);
    else { sideHidden = n; split.setFirstHidden(n); }
    foldersBtn.hidden = !n || Boolean(bar); // v novém ovládání jsou složky pod ☰ v záhlaví
    layout();
    if (bar) refreshRibbon();
  });
  ro.observe(el);

  function layout(): void {
    clear(content);
    if (bar) remeasure(); // jiný režim / šířka → jiná výška řádků
    if (bar) { // náhled jen v širokém okně na PC; jinak jen seznam (otevřít = okno zprávy, na telefonu přes celou obrazovku)
      detailOn = wantDetail();
      content.className = 'mail-content pos-' + (detailOn ? previewPos : 'off');
      if (!detailOn) {
        content.append(listWrap);
        return;
      }
    } else content.className = 'mail-content pos-' + (narrow ? 'narrow' : previewPos);
    if (narrow && !bar) {
      content.append(showingPreview ? preview : listWrap);
      return;
    }
    if (previewPos === 'right') {
      clear(inner.first); clear(inner.second);
      inner.first.appendChild(listWrap);
      inner.second.appendChild(preview);
      inner.defaultSize = Math.max(320, Math.round((content.clientWidth || 800) * 0.45));
      const w = Number(prefs['listWidth']);
      inner.setWidth(w >= 120 ? w : inner.defaultSize);
      content.appendChild(inner);
    } else if (previewPos === 'bottom') {
      clear(innerV.first); clear(innerV.second);
      innerV.first.appendChild(listWrap);
      innerV.second.appendChild(preview);
      innerV.defaultSize = Math.max(160, Math.round((content.clientHeight || 600) * 0.45));
      const hh = Number(prefs['listHeight']);
      innerV.setHeight(hh >= 120 ? hh : innerV.defaultSize);
      content.appendChild(innerV);
    } else {
      content.append(listWrap);
    }
  }

  // ---- pomocné
  const savePref = (key: string, value: unknown) => { prefs[key] = value; void action(desc.app, desc.window, 'pref', desc.params, { key, value }).catch(() => undefined); };
  const accountOf = (id: string): Account | undefined => accounts.find((a) => a.id === id);
  const folderOf = (id: string): { acc: Account; f: Folder } | null => { for (const a of accounts) { const f = a.folders.find((x) => x.id === id); if (f) return { acc: a, f }; } return null; };
  /** today | yesterday | week | last_week | month | older – podle místního dne; starší bez data = older */
  function dateGroup(d: string | null): string {
    if (!d) return 'older';
    const x = new Date(d.replace(' ', 'T') + 'Z');
    const now = new Date();
    const day = (v: Date) => new Date(v.getFullYear(), v.getMonth(), v.getDate()).getTime();
    const diff = Math.round((day(now) - day(x)) / 86400000);
    if (diff <= 0) return 'today';
    if (diff === 1) return 'yesterday';
    const dow = (now.getDay() + 6) % 7; // pondělí = 0
    if (diff <= dow) return 'week';
    if (diff <= dow + 7) return 'last_week';
    if (x.getFullYear() === now.getFullYear() && x.getMonth() === now.getMonth()) return 'month';
    return 'older';
  }
  const fmtListDate = (d: string | null): string => { if (!d) return ''; const x = new Date(d.replace(' ', 'T') + 'Z'); const now = new Date(); return x.toDateString() === now.toDateString() ? fmtTime(x) : fmtDate(x); };

  // ---- levý panel
  /** Proužek nad seznamem: automatická odpověď zapnutá (per účet) s tlačítkem Vypnout. */
  function renderBanners(): void {
    clear(banners);
    for (const a of accounts) {
      if (!a.autoreply) continue;
      const until = a.autoreply.until ? fmtDate(new Date(a.autoreply.until.replace(' ', 'T') + 'Z')) : '';
      const off = h('button', { type: 'button', class: 'btn sm' }, t('posta.ui.autoreply_off'));
      off.addEventListener('click', () => void action(desc.app, desc.window, 'autoreply_off', desc.params, { account: a.id }).then(() => refreshTree()).catch((e: ApiError) => toast(tk(e.message, e.params ?? undefined), 'error')));
      banners.appendChild(h('div', { class: 'mail-banner mail-banner-ar' }, t(until ? 'posta.ui.autoreply_on_until' : 'posta.ui.autoreply_on', { account: a.name, until }), ' ', off));
    }
    if (folderOf(folderKey)?.acc.level === 'read') banners.appendChild(h('div', { class: 'mail-banner mail-banner-ro' }, icon('lock', 'icon'), ' ', t('posta.ui.read_only_folder')));
  }
  function renderSide(): void {
    renderBanners();
    clear(side);
    const allUnread = accounts.reduce((n, a) => n + a.folders.filter((f) => f.role === 'inbox').reduce((m, f) => m + f.unread, 0), 0);
    const all = h('div', { class: 'mail-folder all' + (folderKey === 'all' ? ' active' : ''), tabindex: '0' }, icon('inbox', 'icon'), h('span', { class: 'mail-folder-name' }, t('posta.ui.all_inbox')), allUnread ? h('span', { class: 'mail-unread' }, String(allUnread)) : null);
    all.addEventListener('click', () => selectFolder('all'));
    side.appendChild(all);
    if (outboxCount > 0 || folderKey === 'outbox') { // jako Outlook: jen když něco čeká nebo neodešlo
      const out = h('div', { class: 'mail-folder outbox' + (folderKey === 'outbox' ? ' active' : ''), tabindex: '0' }, icon('send', 'icon'), h('span', { class: 'mail-folder-name' }, t('posta.send.outbox')), outboxCount ? h('span', { class: 'mail-unread' }, String(outboxCount)) : null);
      out.addEventListener('click', () => selectFolder('outbox'));
      side.appendChild(out);
    }
    if (accounts.length === 0) {
      side.appendChild(h('div', { class: 'mail-side-empty muted small' }, t('posta.ui.no_accounts'), p.canManage ? h('button', { type: 'button', class: 'btn sm', onClick: () => void wm.open({ app: 'posta', window: 'ucet', modal: true, parent: win }) }, icon('plus', 'icon'), ' ', t('posta.ui.add_account')) : null));
    }
    let sharedHead = false;
    for (const a of accounts) {
      if (a.shared && !sharedHead) { // složky, které se mnou sdílí kolegové (čtení / zápis)
        sharedHead = true;
        side.appendChild(h('div', { class: 'mail-side-head muted small' }, t('posta.share.with_me')));
      }
      const isCollapsed = collapsed.has(a.id);
      const unread = a.folders.filter((f) => f.role === 'inbox').reduce((m, f) => m + f.unread, 0);
      const stateIcon = a.syncState === 'error' ? icon('alert', 'icon mail-sync-error') : a.syncState === 'new' ? icon('refresh', 'icon spin muted') : null;
      const head = a.shared
        ? h('div', { class: 'mail-account' + (isCollapsed ? ' collapsed' : ''), title: t(a.level === 'read' ? 'posta.share.read_title' : 'posta.share.write_title', { by: a.sharedBy ?? '', email: a.email }) }, icon('chevron', 'icon chev'), icon('users', 'icon'), h('span', { class: 'mail-account-name' }, a.sharedBy || a.email))
        : h('div', { class: 'mail-account' + (isCollapsed ? ' collapsed' : ''), title: a.email + (a.syncError ? '\n' + a.syncError : '') }, icon('chevron', 'icon chev'), h('span', { class: 'cal-dot', style: { background: a.color || '#1f6fd0' } }), h('span', { class: 'mail-account-name' }, a.name), stateIcon, unread && isCollapsed ? h('span', { class: 'mail-unread' }, String(unread)) : null);
      head.addEventListener('click', () => { if (collapsed.has(a.id)) collapsed.delete(a.id); else collapsed.add(a.id); savePref('collapsed', Array.from(collapsed)); renderSide(); });
      if (!a.shared) contextMenu(head, () => [
        { label: t('posta.cmd.new_folder'), icon: 'folder', disabled: a.level === 'read', onSelect: () => void newFolder(a.id, null) },
        { label: t('posta.cmd.sync'), icon: 'refresh', onSelect: () => void syncNow(a.id) },
        { label: t('posta.cmd.accounts'), icon: 'settings', onSelect: () => void wm.open({ app: 'posta', window: 'ucet', params: { id: a.id }, modal: true, parent: win }) },
      ]);
      side.appendChild(head);
      if (isCollapsed) continue;
      const children = new Map<string | null, Folder[]>();
      for (const f of a.folders) { const key = f.parent && a.folders.some((x) => x.id === f.parent) ? f.parent : null; const arr = children.get(key) ?? []; arr.push(f); children.set(key, arr); }
      const unreadDeep = (f: Folder): number => f.unread + (children.get(f.id) ?? []).reduce((n, c) => n + unreadDeep(c), 0);
      const renderFolder = (f: Folder, depth: number): void => {
        const kids = children.get(f.id) ?? [];
        const closed = kids.length > 0 && collapsedFolders.has(f.id);
        const unread = closed ? unreadDeep(f) : f.unread;
        const chev = kids.length ? h('span', { class: 'chev-box' + (closed ? '' : ' open') }, icon('chevron', 'icon chev')) : h('span', { class: 'chev-box' });
        const row = h('div', { class: 'mail-folder' + (folderKey === f.id ? ' active' : '') + (unread ? ' has-unread' : ''), style: { paddingLeft: 4 + depth * 14 + 'px' }, tabindex: '0', dataset: { folder: f.id } }, chev, icon(ROLE_ICON[f.role] ?? 'folder', 'icon'), h('span', { class: 'mail-folder-name' }, folderName(f)), f.shared ? icon('users', 'icon tiny muted') : null, unread ? h('span', { class: 'mail-unread' }, String(unread)) : null);
        const toggle = () => { if (!kids.length) return; if (collapsedFolders.has(f.id)) collapsedFolders.delete(f.id); else collapsedFolders.add(f.id); savePref('collapsedFolders', Array.from(collapsedFolders)); renderSide(); };
        chev.addEventListener('click', (e) => { e.stopPropagation(); toggle(); });
        row.addEventListener('click', () => selectFolder(f.id));
        row.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight' && closed) toggle(); else if (e.key === 'ArrowLeft' && kids.length && !closed) toggle(); else if (e.key === 'Enter') selectFolder(f.id); });
        let hoverTimer = 0;
        row.addEventListener('dragover', (e) => { if (dragIds.length) { e.preventDefault(); row.classList.add('drop'); if (closed && !hoverTimer) hoverTimer = window.setTimeout(() => { hoverTimer = 0; toggle(); }, 700); } }); // sbalená větev se při přetahování po chvilce rozbalí
        row.addEventListener('dragleave', () => { row.classList.remove('drop'); window.clearTimeout(hoverTimer); hoverTimer = 0; });
        row.addEventListener('drop', (e) => { e.preventDefault(); row.classList.remove('drop'); window.clearTimeout(hoverTimer); hoverTimer = 0; if (dragIds.length) void moveTo(dragIds, f.id); });
        // cizí sdílená složka: jen číst / přesouvat (podle práva); spravovat a dál sdílet smí jen uživatelé účtu
        if (!a.shared) contextMenu(row, () => [
          { label: t('posta.cmd.new_subfolder'), icon: 'folder', disabled: a.level === 'read', onSelect: () => void newFolder(a.id, f.id) },
          { label: t('posta.share.share'), icon: 'users', onSelect: () => void wm.open({ app: 'desktop', window: 'sdileni', params: { type: 'posta.slozka', id: f.id, title: folderName(f) }, modal: true, parent: win, onResult: () => void refreshTree() }) },
          ...(f.role === 'custom' && a.level !== 'read' ? [
            { label: t('posta.cmd.rename_folder'), icon: 'edit', onSelect: () => void renameFolder(f) },
            { separator: true, label: '' },
            { label: t('posta.cmd.delete_folder'), icon: 'trash', danger: true, onSelect: () => void deleteFolder(f) },
          ] : []),
        ]);
        side.appendChild(row);
        if (!closed) for (const c of kids) renderFolder(c, depth + 1);
      };
      for (const f of children.get(null) ?? []) renderFolder(f, 0);
      if (!a.shared) side.appendChild(h('div', { class: 'mail-account-state muted small' }, a.syncError ? a.syncError.slice(0, 80) : a.onServer ? t('posta.ui.server_state', { total: a.total }) : t('posta.ui.sync_state', { state: t('posta.sync.' + a.syncState), bodies: a.bodies, total: a.total })));
    }
    if (accounts.length && labels.length && !bar) { // štítky: klik = hledat tag:klíčové slovo ve zvolené složce
      side.appendChild(h('div', { class: 'mail-side-head muted small' }, t('posta.ui.labels')));
      for (const l of labels) {
        const active = bar ? labelKw === l.keyword : q === 'tag:' + l.keyword;
        const row = h('div', { class: 'mail-folder mail-label-row' + (active ? ' active' : ''), tabindex: '0' }, h('span', { class: 'cal-dot small', style: { background: l.color } }), h('span', { class: 'mail-folder-name' }, l.name));
        row.addEventListener('click', () => {
          if (bar) { // štítek = filtr nad seznamem (štítek s barvou), hledání zůstává
            setLabel(active ? '' : l.keyword);
            if (narrow) setSide(true);
            return;
          }
          search.value = active ? '' : 'tag:' + l.keyword; q = search.value; page = 1; void load(); renderSide();
        });
        contextMenu(row, () => [{ label: t('posta.cmd.manage_labels'), icon: 'settings', onSelect: () => void manageLabels() }]);
        side.appendChild(row);
      }
    }
  }

  // ---- import .eml přetažením do seznamu (do otevřené složky; ve Vše do Doručené prvního účtu)
  async function importEml(files: File[]): Promise<void> {
    const eml = files.filter((f) => /\.eml$/i.test(f.name) || f.type === 'message/rfc822');
    if (!eml.length) { toast(t('posta.ui.import_only_eml'), 'warn'); return; }
    try {
      const r = (await upload('', eml, undefined, true)) as { items?: { id: string }[] } | { id: string }[];
      const items = Array.isArray(r) ? r : (r.items ?? []);
      const res = await action<{ imported: number; toast: string }>(desc.app, desc.window, 'import', desc.params, { folder: folderKey, files: items.map((i) => i.id) });
      toast(res.toast, 'ok');
      await load();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  /** Položky z plochy nebo ze Souborů (vnitřní přetažení): server je přečte z úložiště, soubory zůstanou na místě. */
  async function importStored(ids: string[]): Promise<void> {
    try {
      const res = await action<{ imported: number; toast: string }>(desc.app, desc.window, 'import', desc.params, { folder: folderKey, files: ids });
      toast(res.toast, 'ok');
      await load();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  const importable = (e: DragEvent): boolean => Boolean(e.dataTransfer?.types.includes('Files') || e.dataTransfer?.types.includes('application/x-iwan-items')) && !dragIds.length;
  listWrap.addEventListener('dragover', (e) => { if (importable(e)) { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; listWrap.classList.add('drop'); } });
  listWrap.addEventListener('dragleave', (e) => { if (!listWrap.contains(e.relatedTarget as Node | null)) listWrap.classList.remove('drop'); });
  listWrap.addEventListener('drop', (e) => {
    listWrap.classList.remove('drop');
    const items = e.dataTransfer?.getData('application/x-iwan-items');
    if (items && !dragIds.length) { e.preventDefault(); void importStored(JSON.parse(items) as string[]); return; }
    if (e.dataTransfer?.files.length) { e.preventDefault(); void importEml(Array.from(e.dataTransfer.files)); }
  });

  // ---- seznam
  const search = h('input', { class: 'input mail-search', type: 'search', placeholder: t('posta.ui.search') }) as HTMLInputElement;
  const filterSel = h('select', { class: 'input mail-filter' }, ...(['', 'unread', 'flagged', 'attachments'] as const).map((f) => h('option', { value: f }, t('posta.cmd.filter_' + (f || 'all'))))) as HTMLSelectElement;
  const countEl = h('span', { class: 'muted small mail-count' });
  const prevBtn = h('button', { type: 'button', class: 'btn ghost sm', title: t('posta.ui.prev') }, icon('chevron-left', 'icon'));
  const nextBtn = h('button', { type: 'button', class: 'btn ghost sm', title: t('posta.ui.next') }, icon('chevron', 'icon'));
  const backBtn = h('button', { type: 'button', class: 'btn ghost sm mail-back', hidden: true }, icon('chevron-left', 'icon'));
  const prevNarrow = h('button', { type: 'button', class: 'btn ghost sm', title: t('posta.cmd.prev_msg') }, icon('chevron-up', 'icon')) as HTMLButtonElement;
  const nextNarrow = h('button', { type: 'button', class: 'btn ghost sm', title: t('posta.cmd.next_msg') }, icon('chevron-down', 'icon')) as HTMLButtonElement;
  prevNarrow.addEventListener('click', () => void goTo(cursorIndex() - 1));
  nextNarrow.addEventListener('click', () => void goTo(cursorIndex() + 1));
  const navBar = h('div', { class: 'mail-nav' }, backBtn, prevNarrow, nextNarrow); // úzké okno: zpět + šipky nad zprávou
  // úzké zobrazení: strom složek je vysouvací panel, otevře ho tlačítko v hlavičce seznamu (výběr složky ho zase schová)
  const foldersBtn = h('button', { type: 'button', class: 'btn ghost sm mail-folders-btn', title: t('posta.ui.folders'), hidden: true }, icon('menu', 'icon'));
  foldersBtn.addEventListener('click', () => { sideHidden = !sideHidden; split.setFirstHidden(sideHidden); });
  const sortBtn = h('button', { type: 'button', class: 'btn ghost sm mail-sort', title: t('posta.cmd.sort') }, icon('sort', 'icon'), h('span', { class: 'mail-sort-label' }));
  sortBtn.addEventListener('click', () => IwMenu.showAt(sortMenu(), sortBtn));
  const listHead = h('div', { class: 'mail-list-head' }, foldersBtn, search, filterSel, sortBtn, h('div', { class: 'mail-pager' }, countEl, prevBtn, nextBtn));
  const list = h('div', { class: 'mail-list', tabindex: '0', role: 'listbox' });
  // nové ovládání: hledání v záhlaví okna, filtry jako štítky nad seznamem, řazení v ⚙, počet dole ve stavovém řádku
  const filtersEl = h('div', { class: 'list-filters' });
  const statusEl = h('div', { class: 'list-status' });
  const hs = bar ? new HeadSearch(() => setQuery()) : null;
  if (hs) hs.input.title = t('posta.ui.search');
  const selAllEl = h('div', { class: 'select-all-bar', hidden: true });
  let allIds: string[] | null = null; // „Vybrat všech N“: id celé složky ze serveru (výběr i mimo načtené řádky)
  if (bar) {
    listWrap.append(banners, list);
    split.second.prepend(filtersEl, selAllEl); // jako v Kontaktech přes celou šířku (seznam i náhled)
    split.second.append(statusEl);
    split.second.classList.add('mail-main');
  } else listWrap.append(banners, listHead, list);
  /** Hledání = štítek (tag:) + text z hledání v záhlaví; server zná obojí najednou. */
  function setQuery(): void {
    q = [labelKw ? 'tag:' + labelKw : '', hs?.value() ?? ''].filter(Boolean).join(' ');
    page = 1;
    selected.clear(); cursor = null;
    renderFilters();
    void load();
  }
  function setLabel(kw: string): void {
    labelKw = kw;
    renderSide();
    setQuery();
  }
  /** Štítky filtrů nad seznamem (vzor Kontakty): Stav ▾ · Štítek ▾ · Zrušit filtry. */
  function renderFilters(): void {
    if (!bar) return;
    const states = ['', 'unread', 'flagged', 'attachments'];
    const label = labels.find((l) => l.keyword === labelKw);
    const chips: HTMLElement[] = [
      filterChip('__f_state', t('posta.ui.filter_state') + (filter ? ': ' + t('posta.cmd.filter_' + filter) : ''), Boolean(filter), (b) => IwMenu.showAt([
        { label: t('core.ui.all_short'), checked: !filter, onSelect: () => setFilter('') },
        { separator: true, label: '' },
        ...states.filter(Boolean).map((f): MenuItem => ({ label: t('posta.cmd.filter_' + f), checked: filter === f, onSelect: () => setFilter(f) })),
      ], b)),
    ];
    if (labels.length) chips.push(filterChip('__f_label', t('posta.ui.filter_label') + (label ? ': ' + label.name : ''), Boolean(label), (b) => IwMenu.showAt([
      { label: t('core.ui.all_short'), checked: !labelKw, onSelect: () => setLabel('') },
      { separator: true, label: '' },
      ...labels.map((l): MenuItem => ({ label: l.name, color: l.color, checked: l.keyword === labelKw, onSelect: () => setLabel(l.keyword) })),
      { separator: true, label: '' },
      { label: t('posta.cmd.manage_labels'), icon: 'settings', onSelect: () => void manageLabels() },
    ], b), label?.color));
    if (filter || labelKw) chips.push(clearFiltersChip(() => { filter = ''; labelKw = ''; renderSide(); setQuery(); }));
    filtersEl.replaceChildren(...chips);
  }
  function setFilter(f: string): void {
    filter = f;
    page = 1;
    renderFilters();
    void load();
  }
  /** Stavový řádek dole (vzor Kontakty): počet · vybráno · dočítání / nápověda Otevřít. */
  function renderStatus(): void {
    if (!bar) return;
    const hint = !detailOn && !isMobile() && selected.size === 1 ? h('span', { class: 'muted' }, t('core.ui.open_hint')) : '';
    statusEl.replaceChildren(statusBar(t(threads && sortCol === 'date' ? 'posta.ui.count_threads' : 'posta.ui.count', { n: total }), selected.size ? t('core.ui.selected', { count: selected.size }) : '', bar && paged.busy() ? h('span', { class: 'muted' }, t('core.ui.loading_more')) : hint));
  }
  /** Prázdný seznam: jeden stav s cestou dál (Vymazat hledání / Zrušit filtry / Nová zpráva). */
  function emptyBar(): HTMLElement {
    const text = hs?.value() ? t('core.ui.nothing_found', { q: hs.value() }) : t('posta.ui.no_messages');
    const st = emptyState(text, 'inbox');
    const acts: HTMLElement[] = [];
    const btn = (label: string, ic: string, fn: () => void, primary = false) => { const b = h('button', { type: 'button', class: 'btn' + (primary ? ' primary' : '') }, icon(ic), label); b.addEventListener('click', fn); acts.push(b); };
    if (hs?.value()) btn(t('core.ui.clear_search'), 'x', () => { hs.set(''); if (hs.open) hs.toggle(false); else setQuery(); });
    else if (filter || labelKw) btn(t('core.ui.clear_filters'), 'x', () => { filter = ''; labelKw = ''; renderSide(); setQuery(); });
    if (accounts.length) btn(t('posta.cmd.new'), 'plus', () => compose('new'), true);
    if (acts.length) st.appendChild(h('div', { class: 'list-empty-actions' }, acts));
    return st;
  }
  /** Režim výběru (telefon: dlouhý stisk; PC: ⚙ Vybrat položky): klepnutí přidává / ubírá, zpět ho ukončí. */
  function setSelectMode(on: boolean): void {
    if (on && !wm.hasBack(backSel)) backSel = wm.pushBack(() => { backSel = 0; setSelectMode(false); return true; }, win);
    if (!on && backSel) {
      const id = backSel;
      backSel = 0;
      wm.dropBack(id);
    }
    selectMode = on;
    list.classList.toggle('select-mode', on);
    if (!on) selected.clear();
    paintRows();
  }
  function toggleSel(id: string): void {
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    cursor = id;
    if (!selected.size && isMobile()) setSelectMode(false);
    else paintRows();
  }
  search.addEventListener('input', debounce(() => { q = search.value.trim(); page = 1; void load(); }, 350));
  search.addEventListener('keydown', (e) => { if (e.key === 'Enter') { q = search.value.trim(); page = 1; void load(); } });
  filterSel.addEventListener('change', () => { filter = filterSel.value; page = 1; void load(); });
  prevBtn.addEventListener('click', () => { if (page > 1) { page--; void load(); } });
  nextBtn.addEventListener('click', () => { if (page * 50 < total) { page++; void load(); } });
  let dragIds: string[] = [];
  /** Odeslané a Koncepty: odesilatel jste vy, řadí se podle příjemce. */
  const sentLike = (): boolean => { const r = folderKey === 'all:sent' || folderKey === 'all:drafts' ? 'sent' : roleOf(folderKey); return r === 'sent' || r === 'drafts'; };
  const sortParams = () => ({ sort: sortCol === 'from' && sentLike() ? 'to' : sortCol, dir: sortDir, first: sortFirst });
  const sortLabel = (): string => t('posta.sort.' + (sortCol === 'from' && sentLike() ? 'to' : sortCol)) + ' ' + (sortDir === 'desc' ? '↓' : '↑');
  function setSort(patch: { col?: typeof sortCol; dir?: typeof sortDir; first?: typeof sortFirst }): void {
    if (patch.col !== undefined) { if (patch.col === sortCol) sortDir = sortDir === 'desc' ? 'asc' : 'desc'; else { sortCol = patch.col; sortDir = patch.col === 'date' || patch.col === 'size' ? 'desc' : 'asc'; } }
    if (patch.dir !== undefined) sortDir = patch.dir;
    if (patch.first !== undefined) sortFirst = patch.first === sortFirst ? '' : patch.first;
    savePref('sort', { col: sortCol, dir: sortDir, first: sortFirst });
    page = 1;
    void load();
  }
  function sortMenu(): MenuItem[] {
    const cols: typeof sortCol[] = ['date', 'from', 'subject', 'size'];
    return [
      ...cols.map((c): MenuItem => ({ label: t('posta.sort.' + (c === 'from' && sentLike() ? 'to' : c)), checked: sortCol === c, onSelect: () => setSort({ col: c }) })),
      { separator: true, label: '' },
      { label: t('posta.sort.desc'), checked: sortDir === 'desc', onSelect: () => setSort({ dir: 'desc' }) },
      { label: t('posta.sort.asc'), checked: sortDir === 'asc', onSelect: () => setSort({ dir: 'asc' }) },
      { separator: true, label: '' },
      { label: t('posta.sort.first_unread'), icon: 'mail', checked: sortFirst === 'unread', onSelect: () => setSort({ first: 'unread' }) },
      { label: t('posta.sort.first_flagged'), icon: 'star', checked: sortFirst === 'flagged', onSelect: () => setSort({ first: 'flagged' }) },
      { label: t('posta.sort.first_attachments'), icon: 'paperclip', checked: sortFirst === 'attachments', onSelect: () => setSort({ first: 'attachments' }) },
    ];
  }

  const fetchPage = (pg: number) => action<{ rows: Row[]; total: number; page: number; marks?: number[] }>(desc.app, desc.window, 'list', desc.params, { folder: folderKey, q, filter, page: pg, ...sortParams(), threads: threads && sortCol === 'date', ...(bar && pg === 1 && grouped() ? { marks: groupMarks() } : {}) });
  const GROUPS = ['today', 'yesterday', 'week', 'last_week', 'month', 'older'];
  /** Nadpisy Dnes / Včera… (jen řazení podle data bez „nejdřív“). */
  const grouped = (): boolean => groupByDate && sortCol === 'date' && !sortFirst;
  /** Hranice skupin v UTC (stejné jako dateGroup): od dnešní půlnoci, včerejší, pondělí tohoto a minulého týdne, 1. dne měsíce. */
  function groupMarks(): string[] {
    const n = new Date();
    const y = n.getFullYear(), m = n.getMonth(), d = n.getDate(), dow = (n.getDay() + 6) % 7;
    let min = Infinity;
    return [new Date(y, m, d), new Date(y, m, d - 1), new Date(y, m, d - dow), new Date(y, m, d - dow - 7), new Date(y, m, 1)].map((x) => {
      min = Math.min(min, x.getTime()); // skupina dřív v pořadí má přednost (dateGroup) → hranice nesmí růst
      return new Date(min).toISOString().slice(0, 19).replace('T', ' ');
    });
  }
  /** Počty zpráv od každé hranice → pozice nadpisů v seznamu (sestupně: Dnes nahoře, vzestupně: Starší nahoře). */
  function headsFrom(c: number[], tot: number): { pos: number; key: string }[] {
    const out: { pos: number; key: string }[] = [];
    const cum = [...c, tot];
    GROUPS.forEach((key, i) => {
      const prev = i ? (cum[i - 1] ?? 0) : 0;
      const end = cum[i] ?? tot;
      if (end - prev > 0) out.push({ key, pos: sortDir === 'desc' ? prev : tot - end });
    });
    return out.sort((x, z) => x.pos - z.pos);
  }
  /**
   * Načtení seznamu. Nové ovládání (bar): plynulý seznam bez stránek – od začátku, další dávky přidá posouvání (loadMore);
   * keep = obnovit všechny už načtené dávky a zachovat místo (nové zprávy, změny z jiného okna).
   */
  async function load(keep = false): Promise<void> {
    try {
      sortBtn.querySelector('.mail-sort-label')!.textContent = sortLabel();
      if (bar) {
        const seq = ++loadSeq;
        const r = await fetchPage(1);
        if (seq !== loadSeq) return; // mezitím jiná složka / hledání
        total = r.total; page = 1;
        heads = r.marks ? headsFrom(r.marks, r.total) : [];
        paged.reset(r.total, r.rows, keep);
        rows = paged.rows;
        if (!keep) {
          selected.clear();
          allIds = null;
          list.scrollTop = 0;
        } else void pruneSelection(seq, r.rows);
        vwin = { start: -1, end: -1 };
        renderList();
        return;
      }
      const r = await fetchPage(page);
      rows = r.rows; total = r.total; page = r.page;
      for (const id of Array.from(selected)) if (!rows.some((x) => x.id === id)) selected.delete(id);
      renderList();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  /** Po obnovení: vybrané zprávy, které už ve složce nejsou (smazané, přesunuté), z výběru pryč (jako seznamy jádra). */
  async function pruneSelection(seq: number, fresh: Row[]): Promise<void> {
    const have = new Set(fresh.map((r) => r.id));
    if (!selected.size || Array.from(selected).every((id) => have.has(id))) return;
    if (total > 5000) return;
    try {
      const keep = new Set((await action<{ ids: string[] }>(desc.app, desc.window, 'ids', desc.params, { folder: folderKey, q, filter })).ids);
      if (seq !== loadSeq) return;
      let changed = false;
      for (const id of Array.from(selected)) if (!keep.has(id)) { selected.delete(id); changed = true; }
      if (changed) paintRows();
    } catch { /* zkusí se při dalším obnovení */ }
  }
  /** Vykreslit jen okno kolem posunu (+ rezerva); force = i když se rozsah nezměnil (nová data, výběr…). */
  function renderWindow(force: boolean): void {
    if (!bar || !rows.length) return;
    const lh = rowH || (isMobile() ? 65 : 52);
    const hh = headH || 22;
    const hp = heads.map((x) => x.pos);
    const w = headWindow(rows.length, lh, hp, hh, list.scrollTop, list.clientHeight || 600);
    if (!force && w.start === vwin.start && w.end === vwin.end) { paged.ensure(w.start, w.end); return; }
    vwin = w;
    const els: HTMLElement[] = [];
    const lead = heads.filter((x) => x.pos <= w.start).pop();
    // nadpis skupiny první vykreslené zprávy je mimo okno → přilepený nadpis bez vlastní výšky (posun nesedí jinak)
    if (lead && lead.pos < w.start) els.push(h('div', { class: 'mail-group ghost', style: { marginBottom: -hh + 'px' } }, t('posta.group.' + lead.key)));
    for (let i = w.start; i < w.end; i++) {
      const g = heads.find((x) => x.pos === i);
      if (g) els.push(h('div', { class: 'mail-group' }, t('posta.group.' + g.key)));
      const r = rows[i];
      els.push(ph(r) ? h('div', { class: 'mail-row ph', 'aria-hidden': 'true' }, h('div', { class: 'mail-avatar' }), h('div', { class: 'mail-row-main' }, h('div', { class: 'ph-bar' }), h('div', { class: 'ph-bar short' }))) : rowEl(r as Row));
    }
    list.replaceChildren(h('div', { class: 'mail-vpad', style: { height: w.padTop + 'px' } }), ...els, h('div', { class: 'mail-vpad', style: { height: w.padBottom + 'px' } }));
    list.classList.add('virtual');
    // výška řádku a nadpisu podle skutečného vykreslení (písmo, telefon, hustota) – změřit jednou, pak je všechny stejné
    if (!measuring && (!rowH || (!headH && heads.length))) {
      let mr = 0;
      list.querySelectorAll<HTMLElement>('.mail-row:not(.ph)').forEach((x) => { mr = Math.max(mr, x.offsetHeight); });
      const mh = list.querySelector<HTMLElement>('.mail-group:not(.ghost)')?.offsetHeight ?? 0;
      if (mr > 10 || mh > 5) {
        if (mr > 10) { rowH = mr; list.style.setProperty('--mail-row-h', mr + 'px'); }
        if (mh > 5) headH = mh;
        measuring = true;
        try { renderWindow(true); } finally { measuring = false; }
        return;
      }
    }
    paged.ensure(w.start, w.end);
  }
  /** Znovu změřit výšku řádků (přepnutí PC / telefon, hustota). */
  function remeasure(): void {
    rowH = 0;
    headH = 0;
    list.style.removeProperty('--mail-row-h');
    vwin = { start: -1, end: -1 };
  }
  /** Posunout seznam tak, aby byla zpráva na pozici i vidět (pod přilepeným nadpisem). */
  function scrollToRow(i: number): void {
    const lh = rowH || 52;
    const hh = heads.length ? (headH || 22) : 0;
    const top = headOffset(i, lh, heads.map((x) => x.pos), headH || 22);
    if (top - hh < list.scrollTop) list.scrollTop = top - hh;
    else if (top + lh > list.scrollTop + list.clientHeight) list.scrollTop = top + lh - list.clientHeight;
    renderWindow(false);
  }

  /** Náhled při výběru více zpráv: „Vybráno N“ (jako detail v Kontaktech). */
  function previewForSelection(): void {
    if (!bar || !detailOn) return;
    if (selected.size >= 2) {
      current = null;
      preview.replaceChildren(emptyState(t('core.ui.selected', { count: selected.size }), 'mail'));
    } else if (selected.size === 0 && (current === null || !rows.some((r) => r.id === current?.id))) {
      current = null;
      preview.replaceChildren(emptyState(t('posta.ui.select_message'), 'mail'));
    }
  }
  /** Překreslí jen stav řádků (výběr, kurzor, nepřečtené, hvězdička) bez výměny prvků – jinak by dvojklik nikdy nepřišel. */
  function paintRows(): void {
    previewForSelection();
    for (const el of Array.from(list.querySelectorAll<HTMLElement>('.mail-row'))) {
      const r = rows.find((x) => x.id === el.dataset['id']);
      if (!r) continue;
      el.classList.toggle('unread', !r.seen);
      el.classList.toggle('selected', selected.has(r.id));
      el.classList.toggle('cursor', cursor === r.id);
      const star = el.querySelector('.mail-star');
      if (star) { star.classList.toggle('on', r.flagged); star.setAttribute('title', t(r.flagged ? 'posta.cmd.unflag' : 'posta.cmd.flag')); }
    }
    refreshRibbon();
    renderStatus();
    renderSelectAll();
  }
  function renderList(): void {
    clear(list);
    const pages = Math.max(1, Math.ceil(total / 50));
    countEl.textContent = total ? t('posta.ui.count', { n: total }) + (pages > 1 && !bar ? ' · ' + t('posta.ui.page', { page, pages }) : '') : '';
    prevBtn.disabled = page <= 1; nextBtn.disabled = page >= pages;
    prevBtn.hidden = nextBtn.hidden = Boolean(bar); // plynulý seznam: bez stránek
    renderStatus();
    content.classList.toggle('is-empty', Boolean(bar) && rows.length === 0); // prázdný stav přes celé okno, náhled pryč
    if (rows.length === 0) { list.appendChild(bar ? emptyBar() : emptyState(t('posta.ui.no_messages'), 'inbox')); refreshRibbon(); renderSelectAll(); return; }
    if (bar) { renderWindow(true); refreshRibbon(); return; } // plynulý seznam: jen okno kolem posunu
    let lastGroup: string | null = null;
    for (const r of rows) {
      if (groupByDate && sortCol === 'date') { // skupina podle dne (místní čas)
        const g = dateGroup(r.date);
        if (g !== lastGroup) { lastGroup = g; list.appendChild(h('div', { class: 'mail-group' }, t('posta.group.' + g))); }
      }
      list.appendChild(rowEl(r));
    }
    refreshRibbon();
  }
  /** Řádek zprávy (klik, dvojklik, přetažení, nabídka / dlouhý stisk). */
  function rowEl(r: Row): HTMLElement {
    const star = h('button', { type: 'button', class: 'mail-star' + (r.flagged ? ' on' : ''), title: t(r.flagged ? 'posta.cmd.unflag' : 'posta.cmd.flag') }, icon('star', 'icon'));
    if (r.folder === 'outbox') star.hidden = true; // ve frontě odesílání nic neoznačovat
    star.addEventListener('click', (e) => { e.stopPropagation(); void setFlags([r.id], { flagged: !r.flagged }); });
    const row = h('div', { class: 'mail-row' + (r.seen ? '' : ' unread') + (selected.has(r.id) ? ' selected' : '') + (cursor === r.id ? ' cursor' : ''), role: 'option', draggable: 'true', dataset: { id: r.id } },
      h('div', { class: 'mail-avatar', style: { background: `hsl(${hue(r.fromEmail || r.from)} 45% 55%)` } }, initials(r.from)),
      bar ? h('div', { class: 'mail-check', 'aria-hidden': 'true' }, icon('check', 'icon')) : null, // režim výběru: místo avataru zaškrtnutí
      h('div', { class: 'mail-row-main' },
        h('div', { class: 'mail-row-top' }, h('span', { class: 'mail-from' }, r.thread && r.thread.count > 1 ? r.thread.participants.join(', ') : (r.folder === 'outbox' || folderOf(r.folder)?.f.role === 'sent' || folderOf(r.folder)?.f.role === 'drafts' ? (r.to.join(', ') || r.from) : r.from), r.thread && r.thread.count > 1 ? h('span', { class: 'mail-thread-count' }, String(r.thread.count)) : null), h('span', { class: 'mail-date muted' }, fmtListDate(r.date))),
        h('div', { class: 'mail-row-bottom' }, r.send ? sendIcon(r.send) : null, r.priority === 'high' ? h('span', { class: 'mail-prio', title: t('posta.napsat.prio_high') }, icon('alert', 'icon tiny')) : null, r.answered ? icon('reply', 'icon tiny muted') : null, r.forwarded ? icon('forward', 'icon tiny muted') : null, ...labelChips(r.keywords, labels), h('span', { class: 'mail-subject' }, r.subject || t('posta.ui.no_subject')), h('span', { class: 'mail-preview-text muted' }, r.preview ? ' – ' + r.preview : ''), r.hasAttachments ? icon('paperclip', 'icon tiny muted') : null, folderKey === 'all' && accounts.length > 1 ? h('span', { class: 'cal-dot small', style: { background: r.accountColor || '#1f6fd0' }, title: r.accountName }) : null),
      ),
      star,
    );
    row.addEventListener('click', (e) => {
      if (Date.now() < suppressTap) return; // dlouhý stisk právě zapnul výběr
      if (bar && selectMode) { toggleSel(r.id); return; }
      if (e.ctrlKey || e.metaKey) { if (selected.has(r.id)) selected.delete(r.id); else selected.add(r.id); cursor = r.id; paintRows(); return; }
      if (e.shiftKey && cursor) { // rozsah podle pozice – i přes nenačtené zprávy (dočtou se)
        const a = rows.findIndex((x) => x.id === cursor);
        const b = rows.findIndex((x) => x.id === r.id);
        void rangeIds(() => rows as unknown as CoreRow[], 'id', a, b, bar ? (f, to) => paged.loadRange(f, to) : null).then((ids) => { selected.clear(); for (const id of ids) selected.add(id); paintRows(); });
        return;
      }
      if (e.detail > 1) return; // druhý klik dvojkliku: nic nepřekreslovat
      selected.clear(); selected.add(r.id); cursor = r.id; paintRows();
      if (bar && isMobile()) { // telefon: zpráva jako samostatná stránka, zpět ji zavře; výběr se jako v Kontaktech nedrží
        openWindow(r.id);
        window.setTimeout(() => { selected.clear(); paintRows(); }, 0);
        return;
      }
      void open(r.id);
    });
    row.addEventListener('dblclick', () => openWindow(r.id));
    row.addEventListener('dragstart', (e) => {
      dragIds = selected.has(r.id) ? Array.from(selected) : [r.id];
      if (anyReadOnly(dragIds)) dragIds = []; // ze sdílené složky jen ke čtení nejde přesouvat
      e.dataTransfer?.setData('text/plain', dragIds.join(','));
      // do Souborů / na plochu: kopie .eml (cíl doplní složku a zavolá akci okna)
      e.dataTransfer?.setData('application/x-iwan-export', JSON.stringify({ app: desc.app, window: desc.window, action: 'export', params: desc.params, payload: { ids: dragIds } }));
    });
    row.addEventListener('dragend', () => { dragIds = []; });
    contextMenu(row, (e) => {
      if (bar && 'pointerType' in e && e.pointerType === 'touch') { // dlouhý stisk = režim výběru (jako Kontakty)
        suppressTap = Date.now() + 1200;
        e.preventDefault();
        if (!selectMode) setSelectMode(true);
        if (!selected.has(r.id)) toggleSel(r.id);
        return null;
      }
      if (bar && Date.now() < suppressTap) { e.preventDefault(); return null; }
      if (!selected.has(r.id)) { selected.clear(); selected.add(r.id); cursor = r.id; renderList(); }
      return rowMenu();
    });
    return row;
  }

  function rowMenu(): MenuItem[] {
    const ids = Array.from(selected);
    const first = rows.find((x) => x.id === ids[0]);
    const anyUnseen = rows.some((x) => ids.includes(x.id) && !x.seen);
    const anyUnflagged = rows.some((x) => ids.includes(x.id) && !x.flagged);
    const ro = anyReadOnly(ids); // sdílená složka jen ke čtení: přečteno je moje (smí), zápis ne
    const single = ids.length === 1;
    const items: MenuItem[] = [
      ...(bar ? [{ label: t('posta.cmd.new'), icon: 'plus', shortcut: 'Ctrl+N', disabled: !accounts.length, onSelect: () => compose('new') }, { separator: true, label: '' }] satisfies MenuItem[] : []),
      ...(bar && single ? [
        { label: t('posta.cmd.reply'), icon: 'reply', onSelect: () => compose('reply') },
        { label: t('posta.cmd.reply_all'), icon: 'reply-all', onSelect: () => compose('reply_all') },
        { label: t('posta.cmd.forward'), icon: 'forward', onSelect: () => compose('forward') },
        { separator: true, label: '' },
      ] satisfies MenuItem[] : []),
      { label: t('posta.cmd.open_window'), icon: 'window', disabled: !single, onSelect: () => { if (first) openWindow(first.id); } },
      ...(bar && single && current?.printUrl && current.id === first?.id ? [{ label: t('posta.cmd.print'), icon: 'printer', shortcut: 'Ctrl+P', onSelect: () => { if (current) printMessage(current, prefix, remoteShown()); } }] satisfies MenuItem[] : []),
      { label: t(anyUnseen ? 'posta.cmd.seen' : 'posta.cmd.unseen'), icon: 'check', onSelect: () => void setFlags(ids, { seen: anyUnseen }) },
      ...(ro ? [] : [
        { label: t(anyUnflagged ? 'posta.cmd.flag' : 'posta.cmd.unflag'), icon: 'star', onSelect: () => void setFlags(ids, { flagged: anyUnflagged }) },
        { label: t('posta.cmd.move'), icon: 'folder', children: moveMenu(ids) },
        { label: t('posta.cmd.labels'), icon: 'tag', children: labelMenu(ids) },
      ] satisfies MenuItem[]),
      { separator: true, label: '' },
      { label: t('posta.cmd.forward_attach'), icon: 'paperclip', disabled: ids.length !== 1, onSelect: () => { if (first) { cursor = first.id; compose('forward_attach'); } } },
      { label: t('posta.cmd.source'), icon: 'code', disabled: ids.length !== 1, onSelect: () => { if (first && current?.id === first.id) openSource(wm, current); else if (first) void action<Message>(desc.app, desc.window, 'message', desc.params, { id: first.id, markSeen: false }).then((mm) => openSource(wm, mm)); } },
      { label: t('posta.cmd.headers'), icon: 'list', disabled: ids.length !== 1, onSelect: () => { if (first) void openHeaders(wm, first.id); } },
      { label: t('posta.cmd.download_raw'), icon: 'download', disabled: ids.length !== 1, onSelect: () => { if (first && current?.id === first.id && current.rawUrl) window.open(current.rawUrl, '_blank'); else if (first) void action<Message>(desc.app, desc.window, 'message', desc.params, { id: first.id, markSeen: false }).then((mm) => { if (mm.rawUrl) window.open(mm.rawUrl, '_blank'); }); } },
      { separator: true, label: '' },
      ...(ro ? [] : [{ label: t('posta.cmd.delete'), icon: 'trash', danger: true, onSelect: () => void remove(ids) }] satisfies MenuItem[]),
      ...(bar ? [{ separator: true, label: '' }, { label: t('core.cmd.select_all'), icon: 'check', shortcut: 'Ctrl+A', onSelect: () => selectAll() }, { label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load(true) }] satisfies MenuItem[] : []),
    ];
    if (!bar) return items;
    // nové ovládání: co pro výběr nejde, v nabídce není (jako seznamy jádra); zdvojené oddělovače pryč
    const out: MenuItem[] = [];
    for (const it of items.filter((x) => !x.disabled)) {
      if (it.separator && (!out.length || out[out.length - 1]?.separator)) continue;
      out.push(it);
    }
    while (out[out.length - 1]?.separator) out.pop();
    return out;
  }
  function selectAll(): void {
    for (const r of rows) if (!ph(r)) selected.add(r.id);
    paintRows();
  }
  /** Vybráno vše načtené a ve složce je víc → nabídnout výběr všech (do 5000, jako Kontakty / Gmail). */
  function renderSelectAll(): void {
    if (!bar) return;
    if (allIds && selected.size < allIds.length) allIds = null; // výběr se mezitím zmenšil
    const loaded = rows.filter((r) => !ph(r));
    const loadedAll = loaded.length > 0 && loaded.every((r) => selected.has(r.id));
    const all = allIds !== null;
    const offer = !all && !threads && selected.size > 1 && loadedAll && total > loaded.length;
    selAllEl.hidden = !all && !offer;
    if (all) {
      const clr = h('button', { type: 'button', class: 'btn sm' }, t('core.ui.clear_selection'));
      clr.addEventListener('click', () => { allIds = null; selected.clear(); paintRows(); });
      selAllEl.replaceChildren(icon('check-square'), h('span', null, t('core.ui.selected_all', { count: allIds?.length ?? 0 })), clr);
      return;
    }
    if (!offer) return;
    const btn = h('button', { type: 'button', class: 'btn sm' }, t('core.ui.select_all_n', { count: total })) as HTMLButtonElement;
    btn.disabled = total > 5000;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        const r = await action<{ ids: string[] }>(desc.app, desc.window, 'ids', desc.params, { folder: folderKey, q, filter });
        allIds = r.ids;
        for (const id of r.ids) selected.add(id);
        paintRows();
      } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
    });
    selAllEl.replaceChildren(icon('check-square'), h('span', null, t('core.ui.selected_of', { count: selected.size, total })), btn);
  }

  const anyReadOnly = (ids: string[]): boolean => rows.some((x) => ids.includes(x.id) && x.readOnly === true);
  function moveMenu(ids: string[]): MenuItem[] {
    const first = rows.find((x) => x.id === ids[0]) ?? current;
    const acc = first ? accountOf(first.account) : undefined;
    if (!acc) return [];
    return acc.folders.filter((f) => f.id !== first?.folder).map((f) => ({ label: folderName(f), icon: ROLE_ICON[f.role] ?? 'folder', onSelect: () => void moveTo(ids, f.id) }));
  }

  /**
   * Je to spam / Není spam (balík C): přesun do Nevyžádané, nebo z ní do Doručené – server při přesunu naučí antispam.
   * Podle složky první vybrané zprávy: v Nevyžádané = „Není spam“, jinde = „Je to spam“.
   */
  function spamTarget(ids: string[]): { spam: boolean; folder: string } | null {
    const first = rows.find((x) => x.id === ids[0]) ?? current;
    const acc = first ? accountOf(first.account) : undefined;
    if (!acc || !first) return null;
    if (rows.some((x) => ids.includes(x.id) && x.account !== first.account)) return null; // zprávy více účtů najednou ne
    const inSpam = acc.folders.find((f) => f.id === first.folder)?.role === 'spam';
    const to = acc.folders.find((f) => f.role === (inSpam ? 'inbox' : 'spam'));
    return to ? { spam: !inSpam, folder: to.id } : null;
  }

  // ---- náhled
  function isDraft(id: string): boolean { const r = rows.find((x) => x.id === id); return r !== undefined && roleOf(r.folder) === 'drafts'; }
  async function open(id: string): Promise<void> {
    if (isDraft(id)) { void wm.open({ app: 'posta', window: 'napsat', params: { mode: 'draft', message: id } }); return; } // koncept: pokračovat v psaní
    if (bar && !detailOn) return; // bez náhledu (úzké okno, telefon): otevírá se okno zprávy
    if (previewPos === 'off' && !narrow) return;
    const trow = rows.find((x) => x.id === id);
    if (threads && trow?.thread && trow.thread.count > 1) { await openThread(id, trow); return; }
    clear(preview);
    // hláška o stahování jen když tělo opravdu chybí a tahá se ze serveru: lokální zpráva přijde dřív než 300 ms
    const loadingTimer = window.setTimeout(() => { if (!preview.childElementCount) preview.appendChild(h('div', { class: 'mail-preview-empty muted' }, icon('refresh', 'icon spin'), ' ', t('posta.ui.loading_body'))); }, 300);
    if (narrow) { showingPreview = true; layout(); }
    try {
      const markRead = String(prefs['markRead'] ?? 'now');
      const m = await action<Message>(desc.app, desc.window, 'message', desc.params, { id, markSeen: markRead === 'now' });
      window.clearTimeout(loadingTimer);
      current = m;
      const row = rows.find((x) => x.id === id);
      if (row && !row.seen && markRead === 'now') { row.seen = true; paintRows(); bumpUnread(row.folder, -1); }
      if (row && !row.seen && markRead === 'delay') { window.setTimeout(() => { if (current?.id === id) void setFlags([id], { seen: true }); }, 2000); }
      renderPreview(m);
      if (m.receiptTo && String(prefs['receipts'] ?? 'never') === 'always') void sendReceipt(m.id);
    } catch (e) {
      window.clearTimeout(loadingTimer);
      clear(preview);
      preview.appendChild(emptyState(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'alert'));
    }
  }

  /** Konverzace: zprávy vlákna pod sebou, nejnovější rozbalená, ostatní jen hlavička (klik rozbalí). */
  async function openThread(id: string, trow: Row): Promise<void> {
    clear(preview);
    const loadingTimer = window.setTimeout(() => { if (!preview.childElementCount) preview.appendChild(h('div', { class: 'mail-preview-empty muted' }, icon('refresh', 'icon spin'), ' ', t('posta.ui.loading_body'))); }, 300);
    if (narrow) { showingPreview = true; layout(); }
    try {
      const markRead = String(prefs['markRead'] ?? 'now');
      const r = await action<{ messages: Message[] }>(desc.app, desc.window, 'thread', desc.params, { id, markSeen: markRead === 'now' });
      window.clearTimeout(loadingTimer);
      clear(preview);
      const last = r.messages[r.messages.length - 1];
      if (!last) return;
      current = last;
      if (markRead === 'now') { let dec = 0; for (const mm of r.messages) if (!mm.seen) dec++; if (trow.thread) { trow.thread.unread = 0; } if (!trow.seen) { trow.seen = true; } paintRows(); if (dec) bumpUnread(trow.folder, -dec); }
      const wrap = h('div', { class: 'mail-thread' }, narrow ? navBar : null, h('div', { class: 'mail-thread-title' }, (last.subject || t('posta.ui.no_subject')).replace(/^((re|fw|fwd|aw|wg|odp)\s*:\s*)+/i, ''), ' ', h('span', { class: 'muted small' }, t('posta.ui.thread_count', { n: r.messages.length }))));
      backBtn.hidden = !narrow;
      backBtn.onclick = () => { showingPreview = false; layout(); };
      r.messages.forEach((mm, i) => {
        const item = h('div', { class: 'mail-thread-item' + (i === r.messages.length - 1 ? ' open' : '') });
        const head = h('div', { class: 'mail-thread-head' }, h('div', { class: 'mail-avatar', style: { background: `hsl(${hue(mm.fromEmail || mm.from)} 45% 55%)` } }, initials(mm.from)),
          h('div', { class: 'mail-thread-meta' }, h('b', null, mm.from), ' ', h('span', { class: 'muted small' }, mm.date ? fmtDate(new Date(mm.date.replace(' ', 'T') + 'Z')) + ' ' + fmtTime(new Date(mm.date.replace(' ', 'T') + 'Z')) : ''), h('div', { class: 'muted small mail-thread-snippet' }, mm.preview)));
        const bodyHost = h('div', { class: 'mail-thread-body' });
        let rendered = false;
        const render = () => { if (rendered) return; rendered = true; const parts = renderMessage(mm, { prefs, prefix, openViewer: (fileId) => openAttachment(wm, mm, fileId), sendReceipt, wm, onItip: (response, calendarId) => void itip(mm.id, response, calendarId), labels }); bodyHost.append(parts.head, parts.body); };
        head.addEventListener('click', () => { item.classList.toggle('open'); if (item.classList.contains('open')) render(); });
        item.append(head, bodyHost);
        if (i === r.messages.length - 1) render();
        wrap.appendChild(item);
      });
      preview.appendChild(wrap);
      refreshRibbon();
    } catch (e) {
      window.clearTimeout(loadingTimer);
      clear(preview);
      preview.appendChild(emptyState(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'alert'));
    }
  }

  function renderPreview(m: Message): void {
    clear(preview);
    backBtn.hidden = !narrow;
    backBtn.onclick = () => { showingPreview = false; layout(); };
    const idx = rows.findIndex((x) => x.id === m.id);
    prevNarrow.disabled = !hasPrevMsg(); nextNarrow.disabled = !hasNextMsg();
    const parts = renderMessage(m, {
      prefs, prefix, lead: narrow ? navBar : null, position: idx >= 0 ? t('posta.ui.position', { i: (bar ? 0 : (page - 1) * 50) + idx + 1, n: total }) : undefined,
      openViewer: (fileId) => openAttachment(wm, m, fileId),
      sendReceipt,
      onNavigate: (dir) => void goTo(cursorIndex() + dir),
      wm,
      onItip: (response, calendarId) => void itip(m.id, response, calendarId),
      labels,
    });
    preview.append(parts.head, parts.body);
    refreshRibbon();
  }

  function openWindow(id: string): void {
    if (isDraft(id)) { void wm.open({ app: 'posta', window: 'napsat', params: { mode: 'draft', message: id } }); return; }
    // samostatné okno zprávy (více zpráv vedle sebe); dostane pořadí aktuálního seznamu pro šipky předchozí/následující
    const nav = navFor(id);
    void wm.open({ app: 'posta', window: 'zprava', params: { id }, restore: nav ? { nav: { ...nav, folder: folderKey, q, filter, ...sortParams(), total } } : undefined });
  }
  /** Pořadí pro šipky v okně zprávy: celé načtené dávky kolem zprávy (zbytek si okno dočte samo). */
  function navFor(id: string): { ids: string[]; pageFrom: number; pageTo: number } | null {
    if (!bar) return { ids: rows.map((r) => r.id), pageFrom: page, pageTo: page };
    const P = 50;
    const i = rows.findIndex((x) => x.id === id);
    if (i < 0) return null;
    const full = (pg: number): boolean => pg >= 0 && pg * P < rows.length && rows.slice(pg * P, (pg + 1) * P).every((r) => !ph(r));
    let a = Math.floor(i / P);
    if (!full(a)) return null;
    let b = a;
    while (full(a - 1)) a--;
    while (full(b + 1)) b++;
    return { ids: rows.slice(a * P, Math.min(rows.length, (b + 1) * P)).map((r) => r.id), pageFrom: a + 1, pageTo: b + 1 };
  }
  function compose(mode: 'new' | 'reply' | 'reply_all' | 'forward' | 'forward_attach'): void {
    const id = mode === 'new' ? null : (current?.id ?? cursor);
    if (mode !== 'new' && !id) return;
    void wm.open({ app: 'posta', window: 'napsat', params: id ? { mode, message: id } : { mode }, newInstance: mode === 'new' }); // každá nová zpráva = nové okno; odpověď na tutéž zprávu se sdílí
  }

  function bumpUnread(folderId: string, delta: number): void {
    const f = folderOf(folderId);
    if (f) { f.f.unread = Math.max(0, f.f.unread + delta); renderSide(); }
  }

  // ---- akce
  /** Jsou v náhledu zobrazené obrázky z internetu (tlačítko Zobrazit obrázky nebo nastavení)? Tisk je pak ukáže taky. */
  const remoteShown = (): boolean => !!preview.querySelector('iframe[src*="images=1"]');
  /** Štítek přidat/odebrat vybraným (v konverzacích celému vláknu); pak překreslit čipy. */
  async function toggleLabel(ids: string[], l: Label): Promise<void> {
    const all = expandIds(ids);
    const allHave = rows.filter((r) => ids.includes(r.id)).every((r) => r.keywords.includes(l.keyword));
    try {
      await action(desc.app, desc.window, 'keywords', desc.params, { ids: all, add: allHave ? [] : [l.keyword], remove: allHave ? [l.keyword] : [] });
      for (const r of rows) if (all.includes(r.id) || ids.includes(r.id)) r.keywords = allHave ? r.keywords.filter((k) => k !== l.keyword) : Array.from(new Set([...r.keywords, l.keyword]));
      if (current && (all.includes(current.id) || ids.includes(current.id))) {
        current.keywords = rows.find((r) => r.id === current?.id)?.keywords ?? current.keywords;
        const subj = preview.querySelector('.mail-msg-subject'); // čipy v hlavičce bez překreslení těla
        if (subj) { subj.querySelectorAll('.mail-label').forEach((x) => x.remove()); subj.append(...labelChips(current.keywords, labels)); }
      }
      renderList();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  function labelMenu(ids: string[]): MenuItem[] {
    const sel = rows.filter((r) => ids.includes(r.id));
    return [
      ...labels.map((l): MenuItem => ({ label: l.name, color: l.color, checked: sel.length > 0 && sel.every((r) => r.keywords.includes(l.keyword)), disabled: !sel.length, onSelect: () => void toggleLabel(ids, l) })),
      { separator: true, label: '' },
      { label: t('posta.cmd.manage_labels'), icon: 'settings', onSelect: () => void manageLabels() },
    ];
  }
  /** Správa štítků: společný seznam jádra (Nastavení → Štítky) – stejné štítky jako Kontakty. */
  function manageLabels(): void {
    void wm.open({ app: 'desktop', window: 'stitky' });
  }

  /** V konverzacích platí akce na všechny zprávy vlákna v této složce. */
  const expandIds = (ids: string[]): string[] => (threads ? Array.from(new Set(ids.flatMap((id) => rows.find((x) => x.id === id)?.thread?.ids ?? [id]))) : ids);
  async function setFlags(ids: string[], flags: { seen?: boolean; flagged?: boolean }): Promise<void> {
    try {
      await action(desc.app, desc.window, 'flags', desc.params, { ids: expandIds(ids), ...flags });
      for (const r of rows) if (ids.includes(r.id)) { if (flags.seen !== undefined) { if (r.seen !== flags.seen) bumpUnread(r.folder, flags.seen ? -1 : 1); r.seen = flags.seen; } if (flags.flagged !== undefined) r.flagged = flags.flagged; }
      if (current && ids.includes(current.id)) { if (flags.flagged !== undefined) current.flagged = flags.flagged; renderPreview(current); }
      paintRows();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function moveTo(ids: string[], folderId: string): Promise<void> {
    try {
      const r = await action<{ moved: number }>(desc.app, desc.window, 'move', desc.params, { ids: expandIds(ids), folder: folderId });
      toast(t('posta.ui.moved', { n: r.moved }), 'ok');
      afterRemoval(ids);
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function remove(ids: string[]): Promise<void> {
    const inTrash = rows.filter((x) => ids.includes(x.id)).every((x) => folderOf(x.folder)?.f.role === 'trash');
    if (inTrash && !(await confirmDelete(undefined, ids.length))) return; // trvale – Enter nesmaže (kontrola 3. 11.)
    try {
      const r = await action<{ deleted: number }>(desc.app, desc.window, 'delete', desc.params, { ids: expandIds(ids) });
      toast(t('posta.ui.deleted', { n: r.deleted }), 'ok');
      afterRemoval(ids);
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  function afterRemoval(ids: string[]): void {
    const idx = rows.findIndex((x) => x.id === cursor);
    if (bar) { // seznam se hned posune; nadpisy skupin s ním, prázdné skupiny zmizí
      const gone = rows.map((x, i) => (ids.includes(x.id) ? i : -1)).filter((i) => i >= 0);
      paged.remove(ids);
      rows = paged.rows;
      total = paged.total;
      const moved = heads.map((x) => ({ ...x, pos: x.pos - gone.filter((i) => i < x.pos).length })).filter((x) => x.pos < total);
      heads = moved.filter((x, k) => moved[k + 1]?.pos !== x.pos);
      vwin = { start: -1, end: -1 };
    } else {
      rows = rows.filter((x) => !ids.includes(x.id));
      total = Math.max(0, total - ids.length);
    }
    for (const id of ids) selected.delete(id);
    if (current && ids.includes(current.id)) { current = null; clear(preview); preview.appendChild(emptyState(t('posta.ui.select_message'), 'mail')); if (narrow) { showingPreview = false; layout(); } }
    const next = rows[Math.min(idx, rows.length - 1)];
    cursor = next && !ph(next) ? next.id : null;
    renderList();
    void refreshTree();
  }
  async function itip(id: string, response: string, calendarId: string | null): Promise<void> {
    try { await action(desc.app, desc.window, 'itip', desc.params, { id, response, calendar: calendarId }); toast(t('posta.itip.done_' + response), 'ok'); if (current?.id === id) void open(id); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function sendReceipt(id: string): Promise<void> {
    try { const r = await action<{ sent: boolean }>(desc.app, desc.window, 'receipt', desc.params, { id }); if (r.sent) toast(t('posta.receipt.sent'), 'ok'); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function syncNow(accountId?: string): Promise<void> {
    try { await action(desc.app, desc.window, 'sync', desc.params, { account: accountId ?? null }); toast(t('posta.sync.queued'), 'ok'); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  const roleOf = (folderId: string): string => { for (const a of accounts) { const f = a.folders.find((x) => x.id === folderId); if (f) return f.role; } return 'custom'; };
  async function newFolder(accountId: string, parent: string | null): Promise<void> {
    const name = await prompt(t('posta.ui.folder_name'), '', { title: t(parent ? 'posta.cmd.new_subfolder' : 'posta.cmd.new_folder') });
    if (!name) return;
    try { await action(desc.app, desc.window, 'folder_create', desc.params, { account: accountId, parent, name }); await refreshTree(); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function renameFolder(f: Folder): Promise<void> {
    const name = await prompt(t('posta.ui.folder_name'), f.name, { title: t('posta.cmd.rename_folder') });
    if (!name || name === f.name) return;
    try { await action(desc.app, desc.window, 'folder_rename', desc.params, { id: f.id, name }); await refreshTree(); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function deleteFolder(f: Folder): Promise<void> {
    if (!(await confirm(t('posta.ui.folder_delete_confirm', { name: f.name }), { danger: true }))) return;
    try { await action(desc.app, desc.window, 'folder_delete', desc.params, { id: f.id }); if (folderKey === f.id) selectFolder('all'); await refreshTree(); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function refreshTree(): Promise<void> {
    try { const r = await action<{ accounts: Account[]; outbox?: number }>(desc.app, desc.window, 'folders', desc.params, {}); accounts = r.accounts; outboxCount = r.outbox ?? 0; renderSide(); } catch { /* ignorovat */ }
  }
  /** Vybrané zprávy z K odeslání v daném stavu (Zkusit znovu / Zrušit). */
  function outSel(ids: string[], states: string[]): boolean {
    return ids.length > 0 && ids.every((id) => { const r = rows.find((x) => x.id === id); return Boolean(r?.send && id.startsWith('out:') && states.includes(r.send.status)); });
  }
  async function outAction(act: 'out_retry' | 'out_cancel', ids: string[]): Promise<void> {
    try {
      for (const id of ids) await action(desc.app, desc.window, act, desc.params, { id });
      toast(t(act === 'out_retry' ? 'posta.send.retried' : 'posta.send.cancelled_toast'), 'ok');
      selected.clear();
      void refreshTree();
      void load(true);
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  function selectFolder(key: string): void {
    if (selectMode) setSelectMode(false);
    folderKey = key; page = 1; selected.clear(); cursor = null;
    if (narrow) setSide(true);
    savePref('lastFolder', key);
    renderSide();
    void load();
    if (narrow) { showingPreview = false; layout(); }
  }

  // ---- ribbon
  const buttons = (): RibbonButton[] => {
    const ids = Array.from(selected);
    const anyUnseen = rows.some((x) => ids.includes(x.id) && !x.seen);
    const anyUnflagged = rows.some((x) => ids.includes(x.id) && !x.flagged);
    const has = ids.length > 0;
    const canWrite = has && !anyReadOnly(ids);
    const one = Boolean(cursor) && (!bar || ids.length === 1); // odpovědět / přeposlat jen nad jednou zprávou
    return [
      { id: 'new', label: t('posta.cmd.new'), icon: 'plus', shortcut: 'Ctrl+N', group: 'core.grp.new', primary: true, disabled: accounts.length === 0, onClick: () => compose('new') },
      { id: 'open', label: t('posta.cmd.open_window'), icon: 'window', group: 'core.grp.new', disabled: !one, onClick: () => { if (cursor) openWindow(cursor); } },
      { id: 'prev', label: t('posta.cmd.prev_msg'), icon: 'chevron-left', shortcut: 'k', group: 'posta.grp.nav', order: -2, disabled: !hasPrevMsg(), onClick: () => void goTo(cursorIndex() - 1) },
      { id: 'next', label: t('posta.cmd.next_msg'), icon: 'chevron', shortcut: 'j', group: 'posta.grp.nav', order: -2, disabled: !hasNextMsg(), onClick: () => void goTo(cursorIndex() + 1) },
      { id: 'source', label: t('posta.cmd.source'), icon: 'code', shortcut: 'Ctrl+U', group: 'core.grp.actions', disabled: !current, onClick: () => { if (current) openSource(wm, current); } },
      { id: 'print', label: t('posta.cmd.print'), icon: 'printer', shortcut: 'Ctrl+P', group: 'core.grp.actions', disabled: !current?.printUrl, onClick: () => { if (current) printMessage(current, prefix, remoteShown()); } },
      { id: 'raw', label: t('posta.cmd.download_raw'), icon: 'download', group: 'core.grp.actions', disabled: !current?.rawUrl, onClick: () => { if (current?.rawUrl) window.open(current.rawUrl, '_blank'); } },
      { id: 'reply', label: t('posta.cmd.reply'), icon: 'reply', group: 'posta.grp.reply', order: 1, disabled: !one, onClick: () => compose('reply') },
      { id: 'reply_all', label: t('posta.cmd.reply_all'), icon: 'reply-all', group: 'posta.grp.reply', order: 1, disabled: !one, onClick: () => compose('reply_all') },
      { id: 'forward', label: t('posta.cmd.forward'), icon: 'forward', group: 'posta.grp.reply', order: 1, disabled: !one, onClick: () => compose('forward') },
      { id: 'forward_attach', label: t('posta.cmd.forward_attach'), icon: 'paperclip', group: 'posta.grp.reply', order: 1, disabled: !one, onClick: () => compose('forward_attach') },
      { id: 'delete', label: t('posta.cmd.delete'), icon: 'trash', group: 'core.grp.manage', danger: true, disabled: !canWrite, onClick: () => void remove(ids) },
      { id: 'out_retry', label: t('posta.send.retry_cmd'), icon: 'refresh', group: 'core.grp.manage', disabled: !outSel(ids, ['failed']), onClick: () => void outAction('out_retry', ids) },
      { id: 'out_cancel', label: t('posta.send.cancel_cmd'), icon: 'x', group: 'core.grp.manage', danger: true, disabled: !outSel(ids, ['queued', 'delayed', 'retry', 'failed']), onClick: () => void outAction('out_cancel', ids) },
      { id: 'move', label: t('posta.cmd.move'), icon: 'folder', group: 'core.grp.manage', disabled: !canWrite, menu: canWrite ? moveMenu(ids) : [] },
      ...((): RibbonButton[] => {
        const sp = canWrite ? spamTarget(ids) : null;
        return [{ id: 'spam', label: t(sp?.spam === false ? 'posta.cmd.not_spam' : 'posta.cmd.is_spam'), icon: sp?.spam === false ? 'check' : 'alert', group: 'core.grp.manage', disabled: !sp, onClick: () => { if (sp) void moveTo(ids, sp.folder); } }];
      })(),
      ...((): RibbonButton[] => { // balík G: odesílatel na listinu schránky (jedna zpráva)
        const r = ids.length === 1 ? rows.find((x) => x.id === ids[0]) : undefined;
        const ok = senderListable(accounts, r);
        return [{ id: 'sender', label: t('posta.cmd.sender'), icon: 'shield', group: 'core.grp.manage', disabled: !ok, menu: ok && r ? senderMenu(r) : [] }];
      })(),
      { id: 'sync', label: t('posta.cmd.sync'), icon: 'refresh', shortcut: bar ? 'F5' : undefined, group: 'core.grp.actions', onClick: () => { void syncNow(); if (bar) void load(true); } },
      { id: 'select_mode', label: t('core.ui.select_mode'), icon: 'check-square', tab: 'view', group: 'core.grp.view', toggle: true, active: selectMode, onClick: () => setSelectMode(!selectMode) },
      // karta Označit
      { id: 'seen', label: t(anyUnseen ? 'posta.cmd.seen' : 'posta.cmd.unseen'), icon: 'check', tab: 'posta.tab.mark', group: 'posta.grp.mark', disabled: !has, onClick: () => void setFlags(ids, { seen: anyUnseen }) },
      { id: 'flag', label: t(anyUnflagged ? 'posta.cmd.flag' : 'posta.cmd.unflag'), icon: 'star', tab: 'posta.tab.mark', group: 'posta.grp.mark', disabled: !canWrite, onClick: () => void setFlags(ids, { flagged: anyUnflagged }) },
      { id: 'labels', label: t('posta.cmd.labels'), icon: 'tag', tab: 'posta.tab.mark', group: 'posta.grp.mark', disabled: bar ? !canWrite : has && !canWrite, menu: labelMenu(ids) },
      { id: 'rules', label: t('posta.cmd.rules'), icon: 'filter', tab: 'posta.tab.mark', group: 'posta.grp.rules', order: 1, onClick: () => void wm.open({ app: 'posta', window: 'pravidla' }) },
      // karta Nastavení
      { id: 'accounts', label: t('posta.cmd.accounts'), icon: 'mail', tab: 'posta.tab.settings', group: 'posta.grp.settings', onClick: () => void wm.open({ app: 'posta', window: 'ucty' }) },
      { id: 'devices', label: t('posta.cmd.devices'), icon: 'devices', tab: 'posta.tab.settings', group: 'posta.grp.settings', onClick: () => void wm.open({ app: 'desktop', window: 'hesla_aplikaci', params: { service: 'mail' } }) },
      { id: 'signatures', label: t('posta.cmd.signatures'), icon: 'edit', tab: 'posta.tab.settings', group: 'posta.grp.settings', onClick: () => void wm.open({ app: 'posta', window: 'podpisy' }) },
      { id: 'settings', label: t('posta.cmd.settings'), icon: 'settings', tab: 'posta.tab.settings', group: 'posta.grp.settings', onClick: () => void wm.open({ app: 'posta', window: 'nastaveni', modal: true, parent: win }) },
      // správce firmy: zprávy zadržené limity odesílání (celá firma)
      ...(p.held !== null && p.held !== undefined ? [{ id: 'held', label: t('posta.zadrzene.title') + (p.held ? ` (${p.held})` : ''), icon: 'alert', tab: 'posta.tab.settings', group: 'posta.grp.settings', onClick: () => void wm.open({ app: 'posta', window: 'zadrzene' }) }] : []),
      { id: 'layers', label: t('kalendar.side'), icon: 'sidebar', tab: 'view', group: 'core.grp.view', toggle: true, active: !sideHidden, onClick: () => setSide(!sideHidden) },
      { id: 'sort', label: bar ? t('core.ui.sort_by') : t('posta.cmd.sort'), icon: 'sort', tab: 'view', group: 'posta.grp.sort', order: 2, menu: sortMenu() },
      { id: 'pv_right', label: t('posta.cmd.preview_right'), icon: 'layout-right', tab: 'view', group: 'posta.grp.preview', order: 1, toggle: true, active: previewPos === 'right', onClick: () => setPreview('right') },
      { id: 'pv_bottom', label: t('posta.cmd.preview_bottom'), icon: 'layout-bottom', tab: 'view', group: 'posta.grp.preview', order: 1, toggle: true, active: previewPos === 'bottom', onClick: () => setPreview('bottom') },
      { id: 'pv_off', label: t('posta.cmd.preview_off'), icon: 'close', tab: 'view', group: 'posta.grp.preview', order: 1, toggle: true, active: previewPos === 'off', onClick: () => setPreview('off') },
      { id: 'group', label: t('posta.cmd.group_by_date'), icon: 'calendar', tab: 'view', group: 'posta.grp.sort', order: 2, toggle: true, active: groupByDate, disabled: sortCol !== 'date', onClick: () => { groupByDate = !groupByDate; savePref('groupByDate', groupByDate); if (bar) void load(true); else renderList(); } },
      { id: 'threads', label: t('posta.cmd.threads'), icon: 'reply-all', tab: 'view', group: 'posta.grp.sort', order: 2, toggle: true, active: threads, disabled: sortCol !== 'date', onClick: () => { threads = !threads; savePref('threads', threads); page = 1; void load(); } },
    ];
  };
  /** Lišta: Nová zpráva · Odpovědět, Odpovědět všem, Přeposlat, Smazat, Přesunout, Označit… (jen se zprávou) · ⋯ · ⚙ zobrazení. */
  function barItems(): BarItem[] {
    const all = buttons();
    const by = (id: string): RibbonButton | undefined => all.find((b) => b.id === id);
    const item = (id: string, place: BarItem['place']): BarItem[] => {
      const b = by(id);
      return b ? [{ id: b.id, label: b.label, icon: b.icon, shortcut: b.shortcut, disabled: b.disabled, danger: b.danger, toggle: b.toggle, active: b.active, menu: b.menu, place, onClick: b.onClick }] : [];
    };
    return [
      ...item('new', 'primary'),
      ...(folderKey === 'outbox' ? ['out_retry', 'out_cancel', 'open'] : ['reply', 'reply_all', 'forward', 'delete', 'move', 'spam', 'seen', 'flag', 'labels', 'open']).flatMap((id) => item(id, 'selection')),
      ...['sender', 'forward_attach', 'source', 'print', 'raw', 'sync', 'rules', 'accounts', 'signatures', 'devices', 'settings', 'held'].flatMap((id) => item(id, 'more')),
      ...(isMobile() ? ['sort', 'group', 'threads', 'select_mode'] : ['sort', 'group', 'threads', 'pv_right', 'pv_bottom', 'pv_off', 'select_mode']).flatMap((id) => item(id, 'view')),
    ];
  }
  function renderHeader(): void {
    if (!bar) return;
    const selecting = selectMode && isMobile();
    renderBarHeader({
      win, wm, bar, fab, search: hs,
      menu: { label: t('posta.ui.folders'), onClick: () => setSide(!sideHidden) },
      select: selecting ? { count: selected.size, onCancel: () => setSelectMode(false), onAll: () => selectAll() } : null,
    });
    // hromadné akce dole: nejdřív to, co se dělá s více zprávami (Smazat, Přesunout, Přečteno, Hvězdička), odpovědi až v Další
    const prio = ['delete', 'move', 'seen', 'flag', 'labels'];
    const rank = (id: string): number => (prio.includes(id) ? prio.indexOf(id) : 99);
    renderSelBar(selBarEl, [...bar.all()].sort((a, b) => rank(a.id) - rank(b.id)), selecting && selected.size > 0);
  }
  const refreshRibbon = () => {
    if (!bar) {
      ribbon.setButtons(buttons());
      return;
    }
    bar.setItems(barItems());
    renderHeader();
  };
  function setPreview(pos: PreviewPos): void { previewPos = pos; savePref('preview', pos); layout(); refreshRibbon(); if (pos !== 'off' && cursor && !current) void open(cursor); }

  /** Přejít na n-tou zprávu seznamu; za okrajem stránky se načte další (nebo předchozí) stránka. */
  async function goTo(n: number): Promise<void> {
    if (bar) { // plynulý seznam: nenačtenou zprávu nejdřív dočíst (End, PgDn přes zástupce)
      if (n < 0 || n >= rows.length) return;
      scrollToRow(n);
      if (ph(rows[n])) await paged.loadRange(n, n + 1);
    } else if (n >= rows.length && page * 50 < total) { page++; await load(); n = 0; }
    else if (n < 0 && page > 1) { page--; await load(); n = rows.length - 1; }
    const r = rows[Math.max(0, Math.min(rows.length - 1, n))];
    if (!r || ph(r)) return;
    cursor = r.id; selected.clear(); selected.add(r.id); paintRows();
    if (!bar) list.querySelector('.mail-row.cursor')?.scrollIntoView({ block: 'nearest' });
    void open(r.id);
  }
  const cursorIndex = (): number => rows.findIndex((x) => x.id === (current?.id ?? cursor));
  const hasPrevMsg = (): boolean => cursorIndex() > 0 || (!bar && page > 1);
  const hasNextMsg = (): boolean => (cursorIndex() >= 0 && cursorIndex() < rows.length - 1) || (!bar && page * 50 < total);

  // ---- klávesnice (vzor Gmail): j/k pohyb, Enter otevřít, # smazat, s hvězdička, u nepřečtené
  list.addEventListener('keydown', (e) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    if (e.key === 'u' && e.ctrlKey && current) { e.preventDefault(); openSource(wm, current); return; }
    if (bar && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') { e.preventDefault(); selectAll(); return; }
    if (e.ctrlKey || e.metaKey || e.altKey) return; // Ctrl+C… nejsou jednopísmenné zkratky pošty
    const i = rows.findIndex((x) => x.id === cursor);
    if (bar) { // jako seznamy jádra: Home/End, PgUp/PgDn, Esc zruší výběr
      const step = Math.max(1, Math.floor(list.clientHeight / (rowH || 56)) - 1);
      if (e.key === 'Home') { e.preventDefault(); void goTo(0); return; }
      if (e.key === 'End') { e.preventDefault(); void goTo(rows.length - 1); return; }
      if (e.key === 'PageDown') { e.preventDefault(); void goTo(Math.min(rows.length - 1, i + step)); return; }
      if (e.key === 'PageUp') { e.preventDefault(); void goTo(Math.max(0, i - step)); return; }
      if (e.key === 'Escape' && selected.size) { e.preventDefault(); e.stopPropagation(); if (selectMode) setSelectMode(false); else { selected.clear(); paintRows(); } return; }
    }
    if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); void goTo(i + 1); }
    else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); void goTo(i - 1); }
    else if (e.key === 'Enter' && cursor) { openWindow(cursor); }
    else if (e.key === 'r' && cursor) { compose('reply'); }
    else if (e.key === 'a' && cursor) { compose('reply_all'); }
    else if (e.key === 'f' && cursor) { compose('forward'); }
    else if (e.key === 'c') { compose('new'); }
    else if ((e.key === '#' || e.key === 'Delete') && selected.size) { if (!anyReadOnly(Array.from(selected))) void remove(Array.from(selected)); }
    else if (e.key === 's' && cursor && !anyReadOnly([cursor])) { const r = rows.find((x) => x.id === cursor); if (r) void setFlags([r.id], { flagged: !r.flagged }); }
    else if (e.key === 'u' && cursor) { const r = rows.find((x) => x.id === cursor); if (r) void setFlags([r.id], { seen: !r.seen }); }
  });

  // ---- události serveru: nové zprávy, změny účtů
  const reloadSoon = debounce(() => { void refreshTree(); void load(true); }, 800);
  const scheduleWindow = (): void => { if (!bar) return; cancelAnimationFrame(vraf); vraf = requestAnimationFrame(() => renderWindow(false)); };
  list.addEventListener('scroll', scheduleWindow, { passive: true });
  new ResizeObserver(scheduleWindow).observe(list);
  const unsub = events.subscribe(['posta.zpravy', 'posta.ucty'], () => reloadSoon(), { onReset: () => reloadSoon() });
  const unsubLabels = events.subscribe(['core.labels'], () => void core<{ labels: Label[] }>('labels', 'list').then((r) => { labels = r.labels; renderFilters(); renderList(); }).catch(() => undefined), {});
  // změna nastavení v okně Nastavení pošty → znovu načíst předvolby (náhled, hustota…)
  const unsubPrefs = events.subscribe(['core.prefs'], () => void action<{ accounts: Account[] }>(desc.app, desc.window, 'folders', desc.params, {}).catch(() => undefined), {});
  void unsubPrefs;

  // ---- start
  renderSide();
  layout();
  renderFilters();
  refreshRibbon();
  preview.appendChild(emptyState(t('posta.ui.select_message'), 'mail'));
  void load().then(() => { if (p.message) { cursor = p.message; selected.add(p.message); renderList(); void open(p.message); } });
  win.setTitle(t('posta.name'), 'mail');

  return {
    el,
    setMode: (mode) => { if (bar) { if (mode !== 'mobile' && selectMode && backSel) setSelectMode(false); layout(); refreshRibbon(); } else ribbon.setMode(mode); },
    getState: () => ({ folder: folderKey }),
    setState: (s) => { const st = s as { folder?: string } | null; if (st?.folder) selectFolder(st.folder); },
    destroy: () => { unsub(); unsubLabels(); ro.disconnect(); },
    shortcut: (sc: string) => {
      if (sc === 'Ctrl+N') { compose('new'); return true; }
      if (sc === 'Ctrl+P' && current?.printUrl) { printMessage(current, prefix, remoteShown()); return true; }
      if (bar && sc === 'Ctrl+F' && hs) { hs.focus(); return true; }
      if (bar && sc === 'F5') { void syncNow(); void load(true); return true; }
      const typing = document.activeElement instanceof HTMLElement && (document.activeElement.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName));
      if (bar && !typing && !list.contains(document.activeElement)) { // klávesy seznamu i po kliknutí jinam v okně
        if (sc === 'Ctrl+A') { selectAll(); return true; }
        if (sc === 'Esc' && selected.size) { if (selectMode) setSelectMode(false); else { selected.clear(); paintRows(); } return true; }
        if (sc === 'Delete' && selected.size && !anyReadOnly(Array.from(selected))) { void remove(Array.from(selected)); return true; }
      }
      return false;
    },
    focus: () => list.focus(),
  };
});

export {};
